import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TAG_NAME_MAX_LENGTH, } from '@wit-support/admin-contracts';
import { Button, toast } from '@wit-support/ui';
import { RailCard, TagChipList } from '@wit-support/ui-domain';
import { Plus, Tags } from 'lucide-react';
import { ModuleRequestError } from '@wit-support/admin-client-core';
import { setConversationTags } from '../api.js';
import { TagPickerDialog } from '../forms/tag-picker-dialog.js';
/**
 * Русские тексты по коду сервера для `PUT …/tags`. Код — машинный
 * (`invalid_name`), и дословно в тосте он ничего оператору не говорит;
 * незнакомый код получает нейтральную фразу, а не сырой JSON.
 */
const TAGS_ERROR_RU = {
    invalid_body: `Тег не длиннее ${TAG_NAME_MAX_LENGTH} символов`,
    invalid_name: 'Недопустимое имя тега',
    not_found: 'Диалог не найден',
    forbidden: 'Недостаточно прав',
};
const TAGS_ERROR_DEFAULT = 'Не удалось сохранить теги';
export function tagsErrorMessage(err) {
    const code = err instanceof ModuleRequestError ? err.code : null;
    return (code != null ? TAGS_ERROR_RU[code] : undefined) ?? TAGS_ERROR_DEFAULT;
}
/**
 * Карточка «Теги» в рейле диалога: заголовок «Теги · N» с компактной «+»,
 * ВСЕ теги чипами (перенос строк, без «+N» — в рейле есть место, а прятать
 * ровно то, ради чего карточка открыта, незачем), снятие крестиком прямо с
 * чипа, добавление — в попапе `TagPickerDialog`.
 *
 * Сохраняет ВЕСЬ набор на каждое изменение (`PUT …/tags`), а не дельту: набор —
 * это одно значение, и «добавить» с «убрать» по отдельности разошлись бы между
 * двумя вкладками, открытыми на одном диалоге. Недостающие имена заводит сервер
 * в той же транзакции, поэтому отдельного «создать тег» здесь нет.
 *
 * Оптимистично правится КЭШ `['conversation', id]`, а не локальное состояние:
 * теги приходят сюда пропом из этого самого запроса, и второе состояние рядом
 * означало бы два источника правды, расходящихся при любом фоновом обновлении.
 */
export function TagsPanel({ conversationId, tags, canEdit }) {
    const queryClient = useQueryClient();
    const [pickerOpen, setPickerOpen] = useState(false);
    const current = tags ?? [];
    const names = current.map((t) => t.name);
    // Порядковый номер последней отправленной записи. Чипы НЕ блокируются на
    // время PUT, так что второе снятие может уйти, пока первое ещё в пути;
    // ответы приходят в любом порядке, и запоздавший ответ на ПЕРВУЮ запись
    // воскресил бы в кэше уже снятый второй тег до тех пор, пока не придёт её
    // собственный ответ. Кэш переписывается только ответом на ПОСЛЕДНЮЮ запись.
    const writeSeq = useRef(0);
    // Ключ мутации — чтобы в `onSettled` спросить у клиента, сколько записей
    // ещё в пути (`isMutating`), а не вести свой счётчик рядом с `writeSeq`.
    const mutationKey = ['conversation-tags', conversationId];
    const mut = useMutation({
        mutationKey,
        mutationFn: (next) => setConversationTags(conversationId, { names: next }),
        onMutate: async (next) => {
            writeSeq.current += 1;
            const seq = writeSeq.current;
            // `exact`, иначе под отмену попадёт ВСЁ поддерево ключа — и лента
            // комментариев, и карточка Stripe: у них с тегами нет ничего общего,
            // а отменённый на полпути запрос просто оставил бы карточку пустой.
            await queryClient.cancelQueries({ queryKey: ['conversation', conversationId], exact: true });
            const previous = queryClient.getQueryData(['conversation', conversationId]);
            queryClient.setQueryData(['conversation', conversationId], (old) => old
                ? {
                    ...old,
                    // Идентификаторов у ещё не сохранённых имён нет — до ответа
                    // сервера берём имя как ключ. Список тут же переписывается
                    // настоящим ответом, так что наружу этот id не живёт.
                    tags: next.map((name) => ({ id: `pending:${name}`, name })),
                }
                : old);
            return { previous, seq };
        },
        onError: (err, _next, ctx) => {
            if (ctx?.previous) {
                queryClient.setQueryData(['conversation', conversationId], ctx.previous);
            }
            toast.error(tagsErrorMessage(err));
        },
        onSuccess: (res, _next, ctx) => {
            if (ctx.seq === writeSeq.current) {
                queryClient.setQueryData(['conversation', conversationId], (old) => old ? { ...old, tags: res.tags } : old);
            }
            // Строка списка показывает те же теги — иначе инбокс отстаёт до перезахода.
            void queryClient.invalidateQueries({ queryKey: ['conversations'] });
            // Словарь: у только что созданного тега появилось использование.
            void queryClient.invalidateQueries({ queryKey: ['tags'] });
        },
        onSettled: () => {
            // Перезапрос диалога — только когда завершилась ПОСЛЕДНЯЯ запись. Две
            // записи в пути: если инвалидировать на КАЖДОМ завершении, перезапрос,
            // запущенный ранним ответом, может вернуться уже ПОСЛЕ позднего и
            // принести набор, в котором второй правки ещё нет, — а `onSuccess`
            // выше с его `writeSeq` этот перезапрос не фильтрует, он только
            // отбрасывает сам поздний ответ PUT. `isMutating` считает и текущую
            // мутацию тоже (`onSettled` идёт ДО перевода в success/error), так что
            // «1» значит «кроме меня никого».
            if (queryClient.isMutating({ mutationKey }) !== 1)
                return;
            void queryClient.invalidateQueries({
                queryKey: ['conversation', conversationId],
                exact: true,
            });
        },
    });
    // Смотреть не на что и делать нечего — карточки нет вовсе. Пустая карточка
    // «Теги» у viewer'а только занимала бы место в рейле.
    if (!canEdit && current.length === 0)
        return null;
    const headerExtra = (_jsxs(_Fragment, { children: [_jsxs("span", { className: "text-xs font-medium tabular-nums", "aria-label": `Всего: ${current.length}`, children: ["\u00B7 ", current.length] }), canEdit ? (_jsx(Button, { type: "button", variant: "ghost", size: "icon", "aria-label": "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u0442\u0435\u0433", title: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u0442\u0435\u0433", className: "-my-1.5 ml-auto h-6 w-6", onClick: () => setPickerOpen(true), children: _jsx(Plus, { className: "h-3.5 w-3.5", "aria-hidden": "true" }) })) : null] }));
    return (_jsxs(RailCard, { icon: Tags, title: "\u0422\u0435\u0433\u0438", headerExtra: headerExtra, children: [current.length === 0 ? (_jsx("p", { className: "text-xs text-[var(--color-text-muted)]", children: "\u0422\u0435\u0433\u043E\u0432 \u043D\u0435\u0442" })) : (_jsx(TagChipList, { tags: current, size: "md", ...(canEdit
                    ? {
                        // Каждое снятие уходит сразу, даже пока предыдущий PUT в пути:
                        // `names` построены от оптимистичного набора (он уже в кэше
                        // через `onMutate`), полная замена набора — «последний
                        // победил», и ответ сервера на последнюю запись переписывает
                        // список целиком. Подтверждение не спрашиваем: снятие тега
                        // обратимо в один клик из попапа.
                        onRemove: (tag) => mut.mutate(names.filter((n) => n !== tag.name)),
                    }
                    : {}) })), canEdit ? (_jsx(TagPickerDialog, { open: pickerOpen, onOpenChange: setPickerOpen, value: names, onSave: (next) => mut.mutate(next) })) : null] }));
}
//# sourceMappingURL=tags-panel.js.map